import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, ChevronRight, Send } from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './App.css';

const API_BASE = '/api';

// Browser key used to remember an in-progress session on the plain URL (no token),
// so a returning customer on the same device/browser can resume their answers.
const LOCAL_SESSION_KEY = 'lifecode_questionnaire_session';

const Form = () => {
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [responses, setResponses] = useState({});
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formError, setFormError] = useState(''); // New state for form-wide error
  const [isNextDisabled, setIsNextDisabled] = useState(false); // Disable Next button if validation fails
  const [firstNameError, setFirstNameError] = useState(''); // Error for first name
  const [lastNameError, setLastNameError] = useState(''); // Error for last name
  const [showPreview, setShowPreview] = useState(false); // Show preview modal
  const [visibleQuestions, setVisibleQuestions] = useState([]); // Track visible questions based on conditional logic
  const [openDropdownId, setOpenDropdownId] = useState(null); // Track which dropdown is currently open
  const [openSingleSelectId, setOpenSingleSelectId] = useState(null); // Track which single-select dropdown is open
  const [showBirthdatePicker, setShowBirthdatePicker] = useState(false); // Track if birthdate datepicker is open
  const [resumed, setResumed] = useState(false); // Whether previously saved answers were restored
  const [initStarted, setInitStarted] = useState(false); // Guard so we only start/resume the session once
  const navigate = useNavigate();
  const location = useLocation();

  // Get pre-fill data from location state (from token access)
  const { sessionId: existingSessionId, prefillData, savedResponses, fromToken } = location.state || {};

  // Load questions on component mount (session start/resume is handled separately
  // once questions are available, so we can restore saved answers correctly).
  useEffect(() => {
    loadQuestions();
  }, []);

  // Plain-URL flow (no token): resume a previous session from this browser if one
  // exists, otherwise start a fresh session. Token flow is handled by the prefill effect.
  useEffect(() => {
    if (questions.length > 0 && !existingSessionId && !sessionId && !initStarted) {
      setInitStarted(true);
      resumeOrStartLocal();
    }
  }, [questions, existingSessionId, sessionId, initStarted]);

  // Clear form error when current question changes
  useEffect(() => {
    setFormError(''); // Clear error on question change
    setIsNextDisabled(false); // Enable next button when moving to a new question
    setFirstNameError(''); // Clear first name error
    setLastNameError(''); // Clear last name error
    setOpenDropdownId(null); // Close any open dropdowns when navigating to a new question
    setOpenSingleSelectId(null); // Close any open single-select dropdowns when navigating to a new question
    setShowBirthdatePicker(false); // Close birthdate picker when navigating to a new question
  }, [currentQuestion]);

  // Recalculate visible questions whenever responses or questions change
  useEffect(() => {
    if (questions.length > 0) {
      const visible = calculateVisibleQuestions(questions, responses);
      setVisibleQuestions(visible);

      // If current question is no longer visible, jump to next visible
      const currentQ = questions[currentQuestion];
      if (currentQ && !visible.find(q => q.id === currentQ.id)) {
        const nextVisible = visible.find(q => q.question_number > currentQ.question_number);
        if (nextVisible) {
          const nextIndex = questions.findIndex(q => q.id === nextVisible.id);
          setCurrentQuestion(nextIndex);
        }
      }
    }
  }, [responses, questions, currentQuestion]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdownId && !event.target.closest('.multiselect-dropdown')) {
        setOpenDropdownId(null);
      }
      if (openSingleSelectId && !event.target.closest('.singleselect-dropdown')) {
        setOpenSingleSelectId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdownId, openSingleSelectId]);

  // Initialize visible questions on load
  useEffect(() => {
    if (questions.length > 0 && visibleQuestions.length === 0) {
      const visible = calculateVisibleQuestions(questions, responses);
      setVisibleQuestions(visible);
    }
  }, [questions]);

  const loadQuestions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/questions`);
      setQuestions(response.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to load questions');
      setLoading(false);
    }
  };

  const startForm = async () => {
    try {
      const sessionResponse = await axios.post(`${API_BASE}/session`);
      const newSessionId = sessionResponse.data.sessionId;
      setSessionId(newSessionId);
      // Remember this session in the browser so the customer can resume on return
      try { localStorage.setItem(LOCAL_SESSION_KEY, newSessionId); } catch (e) { /* storage disabled */ }
    } catch (err) {
      setError('Failed to start form');
    }
  };

  // Plain-URL resume: if this browser has a previous, un-submitted session, restore it;
  // otherwise begin a fresh session.
  const resumeOrStartLocal = async () => {
    let savedSessionId = null;
    try { savedSessionId = localStorage.getItem(LOCAL_SESSION_KEY); } catch (e) { /* storage disabled */ }

    if (savedSessionId) {
      try {
        const res = await axios.get(`${API_BASE}/session/${savedSessionId}/state`);
        if (res.data.valid) {
          setSessionId(savedSessionId);
          // Reuse the same restore logic as the token flow (no kit data on the plain URL)
          applyPrefillData(null, res.data.saved_responses, savedSessionId);
          return;
        }
      } catch (e) {
        // Session no longer exists or was already submitted - fall through to a fresh start
      }
      try { localStorage.removeItem(LOCAL_SESSION_KEY); } catch (e) { /* ignore */ }
    }

    await startForm();
  };

  // Apply pre-fill data when questions are loaded and we arrive via a token link
  useEffect(() => {
    if (questions.length > 0 && existingSessionId && !sessionId) {
      console.log('Applying pre-fill data:', prefillData);
      setSessionId(existingSessionId);
      // Pass existingSessionId directly to avoid race condition with setState
      applyPrefillData(prefillData, savedResponses, existingSessionId);
    }
  }, [questions, prefillData, savedResponses, existingSessionId, sessionId]);

  // Pure check (no component state) used when restoring a resumed session
  const isAnswered = (question, resp) => {
    if (question.field_type === 'compound_name') {
      const first = resp[`${question.id}_first`];
      const last = resp[`${question.id}_last`];
      return first && first.trim() !== '' && last && last.trim() !== '';
    }
    const answer = resp[question.id];
    if (question.field_type === 'checkbox' || question.field_type === 'multiselect') {
      return Array.isArray(answer) && answer.length > 0;
    }
    return answer !== undefined && answer !== null && answer.toString().trim() !== '';
  };

  const applyPrefillData = (data, saved, sessionIdToUse) => {
    const merged = {};

    // Restore previously saved answers so the customer can continue / edit them
    if (saved && typeof saved === 'object') {
      Object.entries(saved).forEach(([qid, ans]) => {
        if (ans === undefined || ans === null || ans === '') return;
        const q = questions.find(q => String(q.id) === String(qid));
        // checkbox / multiselect answers are stored as JSON arrays - parse them back
        if (q && (q.field_type === 'checkbox' || q.field_type === 'multiselect')) {
          try {
            const parsed = JSON.parse(ans);
            merged[qid] = Array.isArray(parsed) ? parsed : ans;
          } catch (e) {
            merged[qid] = ans;
          }
        } else {
          merged[qid] = ans;
        }
      });
    }

    // Q3: Kit Number - always pre-filled from kit data
    const kitQuestion = questions.find(q => q.question_number === 3);
    if (kitQuestion && data && data.kit_number) {
      merged[kitQuestion.id] = data.kit_number;
      saveResponseWithSession(sessionIdToUse, kitQuestion.id, data.kit_number);
    }

    setResponses(merged);
    console.log('Restored responses:', merged);

    // If this is a returning customer with saved answers, jump to where they left off
    const restoredCount = saved ? Object.keys(saved).length : 0;
    if (restoredCount > 0) {
      setResumed(true);
      const visible = calculateVisibleQuestions(questions, merged);
      const firstUnanswered = visible.find(q => !isAnswered(q, merged));
      const target = firstUnanswered || visible[visible.length - 1];
      if (target) {
        const idx = questions.findIndex(q => q.id === target.id);
        if (idx >= 0) setCurrentQuestion(idx);
      }
    }
  };

  // Helper function to check if a field is pre-filled (for read-only styling)
  const isFieldPrefilled = (questionNumber) => {
    return fromToken && questionNumber === 3; // Only Kit Number (Q3) is pre-filled
  };

  const saveResponse = async (questionId, answer) => {
    if (!sessionId) return;

    try {
      await axios.post(`${API_BASE}/response`, {
        sessionId,
        questionId,
        answer
      });
    } catch (err) {
      console.error('Error saving response:', err);
    }
  };

  // Helper to save response with explicit sessionId (for prefilling)
  const saveResponseWithSession = async (sessionIdToUse, questionId, answer) => {
    if (!sessionIdToUse) return;

    try {
      await axios.post(`${API_BASE}/response`, {
        sessionId: sessionIdToUse,
        questionId,
        answer
      });
      console.log('Saved prefill response:', questionId, answer);
    } catch (err) {
      console.error('Error saving prefill response:', err);
    }
  };

  const handleAnswerChange = (value) => {
    const question = questions[currentQuestion];
    const newResponses = { ...responses, [question.id]: value };

    // Check if this answer change affects visibility of other questions
    const affectedQuestions = questions.filter(q => {
      if (!q.conditional_logic) return false;
      const logic = typeof q.conditional_logic === 'string'
        ? JSON.parse(q.conditional_logic)
        : q.conditional_logic;
      return logic.show_if?.question_id === question.id;
    });

    // Mark affected questions as skipped or clear skipped status
    affectedQuestions.forEach(affectedQ => {
      const shouldShow = evaluateCondition(affectedQ, newResponses);
      if (!shouldShow) {
        // Mark as skipped and clear its response
        newResponses[affectedQ.id] = 'N/A - Not Applicable';
        saveResponse(affectedQ.id, 'N/A - Not Applicable');
      } else if (newResponses[affectedQ.id] === 'N/A - Not Applicable') {
        // Clear N/A if question becomes visible again
        delete newResponses[affectedQ.id];
        saveResponse(affectedQ.id, '');
      }
    });

    setResponses(newResponses);

    // Validate the field and set error if invalid
    const errorMessage = validateField(question, value);
    setFormError(errorMessage); // Set error message if validation fails
    setIsNextDisabled(errorMessage !== ''); // Disable next button if there's an error

    setTimeout(() => {
      saveResponse(question.id, value);
    }, 1000);
  };

  // Evaluates whether a question should be shown based on conditional logic
  const evaluateCondition = (question, responses) => {
    // If no conditional logic, always show
    if (!question.conditional_logic) return true;

    const logic = typeof question.conditional_logic === 'string'
      ? JSON.parse(question.conditional_logic)
      : question.conditional_logic;

    if (!logic.show_if) return true;

    const { question_id, operator, value } = logic.show_if;
    const dependencyAnswer = responses[question_id];

    // If dependency not answered yet, hide this question
    if (!dependencyAnswer) return false;

    // Evaluate based on operator
    switch (operator) {
      case 'equals':
        return dependencyAnswer === value;
      case 'not_equals':
        return dependencyAnswer !== value;
      case 'includes': // For multiselect/checkbox
        return Array.isArray(dependencyAnswer) && dependencyAnswer.includes(value);
      case 'not_includes':
        return !Array.isArray(dependencyAnswer) || !dependencyAnswer.includes(value);
      default:
        return true;
    }
  };

  // Calculate which questions should be visible given current responses
  const calculateVisibleQuestions = (allQuestions, responses) => {
    return allQuestions.filter(q => evaluateCondition(q, responses));
  };

  const isCurrentQuestionAnswered = () => {
    const question = questions[currentQuestion];
    const answer = responses[question.id];

    if (!question.is_mandatory) return true;

    // For compound fields like "Name", check both first and last name
    if (question.field_type === 'compound_name') {
      const firstName = responses[`${question.id}_first`];
      const lastName = responses[`${question.id}_last`];
      return firstName && firstName.trim() !== '' && lastName && lastName.trim() !== '' && !firstNameError && !lastNameError;
    }

    // For checkbox and multiselect, check if there are selected options
    if (question.field_type === 'checkbox' || question.field_type === 'multiselect') {
      return answer && Array.isArray(answer) && answer.length > 0;
    }

    // For other types, just check if there's an answer
    return answer && answer.toString().trim() !== '';
  };

  const handleNextClick = () => {
    // Check if the current question is answered and if there's no validation error
    if (!isCurrentQuestionAnswered() || formError !== '') {
      setFormError('This field is required to continue.'); // Show the error
      return; // Prevent moving to the next question
    }

    // Clear the error if the current question is answered
    setFormError('');

    // Find next visible question
    const currentVisibleIndex = visibleQuestions.findIndex(q => q.id === questions[currentQuestion].id);
    const nextVisibleQuestion = visibleQuestions[currentVisibleIndex + 1];

    if (nextVisibleQuestion) {
      const nextIndex = questions.findIndex(q => q.id === nextVisibleQuestion.id);
      setCurrentQuestion(nextIndex);
    }
  };

  const goToPrev = () => {
    // Find previous visible question
    const currentVisibleIndex = visibleQuestions.findIndex(q => q.id === questions[currentQuestion].id);
    const prevVisibleQuestion = visibleQuestions[currentVisibleIndex - 1];

    if (prevVisibleQuestion) {
      const prevIndex = questions.findIndex(q => q.id === prevVisibleQuestion.id);
      setCurrentQuestion(prevIndex);
    }
  };

  const validateAllMandatoryFields = () => {
    const missingFields = [];

    // Only validate visible questions
    const currentlyVisible = calculateVisibleQuestions(questions, responses);

    currentlyVisible.forEach(question => {
      if (!question.is_mandatory) return;

      // Skip if marked as N/A
      const answer = responses[question.id];
      if (answer === 'N/A - Not Applicable') return;

      if (question.field_type === 'compound_name') {
        const firstName = responses[`${question.id}_first`];
        const lastName = responses[`${question.id}_last`];
        const firstError = validateIndividualName(firstName || '');
        const lastError = validateIndividualName(lastName || '');
        if (!firstName || firstName.trim() === '' || !lastName || lastName.trim() === '' || firstError || lastError) {
          missingFields.push(question.question_text);
        }
      } else {
        if (question.field_type === 'checkbox' || question.field_type === 'multiselect') {
          if (!answer || !Array.isArray(answer) || answer.length === 0) {
            missingFields.push(question.question_text);
          }
        } else {
          if (!answer || answer.toString().trim() === '') {
            missingFields.push(question.question_text);
          }
        }
      }
    });

    return missingFields;
  };

  const submitForm = async () => {
    // First validate all mandatory fields on frontend
    const missingFields = validateAllMandatoryFields();

    if (missingFields.length > 0) {
      alert(`Please complete all mandatory fields:\n\n${missingFields.join('\n')}`);
      return;
    }

    try {
      const response = await axios.post(`${API_BASE}/submit`, { sessionId });
      // Form is submitted - clear the remembered session so a return visit starts fresh
      try { localStorage.removeItem(LOCAL_SESSION_KEY); } catch (e) { /* ignore */ }
      alert('Form submitted successfully! Thank you for your responses.');
      navigate('/');
    } catch (err) {
      if (err.response?.data?.missingQuestions) {
        alert(`Please complete all mandatory fields:\n\n${err.response.data.missingQuestions.join('\n')}`);
      } else {
        alert('Error submitting form: ' + (err.response?.data?.message || err.message));
      }
    }
  };

  // Validation functions for various fields
  const validateName = (name) => {
  const regex = /^[a-zA-Z\s]+$/;  // Ensures that the name only contains alphabetic characters and spaces.

  // Check if the name contains anything other than letters and spaces
  if (!regex.test(name)) {
    return 'Name must only contain alphabetic characters and spaces.';
  }

  // Check if the length exceeds 255 characters
  if (name.length > 255) {
    return 'Name must not exceed 255 characters.';
  }

  return ''; // No error
};

  // Validation for individual first/last name
  const validateIndividualName = (name) => {
    if (!name.trim()) return ''; // Allow empty for now, but check in completion
    const regex = /^[a-zA-Z\s]+$/;
    if (!regex.test(name)) {
      return 'Name must only contain alphabetic characters and spaces.';
    }
    if (name.length > 255) {
      return 'Name must not exceed 255 characters.';
    }
    return '';
  };


  const validateEmail = (email) => {
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return regex.test(email) ? '' : 'Invalid email format.';
  };

  const validatePhoneNumber = (phone) => {
    // Allow formats like: +911234567890, +1234567890, 911234567890, or 1234567890
    // With or without + sign, total 10-15 digits
    const regex = /^\+?[0-9]{10,15}$/;
    return regex.test(phone) ? '' : 'Phone number must include country code (e.g., +911234567890) and be 10-15 digits.';
  };

  const validateNumber = (value, question) => {
    // Convert to number and check if it's valid
    const num = parseFloat(value);
    if (isNaN(num)) {
      return 'Please enter a valid number.';
    }

    // Check min/max if specified
    if (question.validation_rules?.min !== undefined && num < question.validation_rules.min) {
      return `Value must be at least ${question.validation_rules.min}.`;
    }
    if (question.validation_rules?.max !== undefined && num > question.validation_rules.max) {
      return `Value must be at most ${question.validation_rules.max}.`;
    }

    return '';
  };

  const validateKitNumber = (kitNumber) => {
    // Only check length - should not exceed 14 characters, allow any characters
    if (kitNumber.length > 14) {
      return 'KIT Number must not exceed 14 characters.';
    }
    return '';
  };

  const validateBirthDate = (dateString) => {
    // Validate dd/mm/yyyy format
    const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const match = dateString.match(regex);

    if (!match) {
      return 'Birth date must be in dd/mm/yyyy format (e.g., 25/12/1990).';
    }

    const day = parseInt(match[1]);
    const month = parseInt(match[2]);
    const year = parseInt(match[3]);

    // Validate month (1-12)
    if (month < 1 || month > 12) {
      return 'Invalid month. Must be between 01-12.';
    }

    // Validate day based on month
    const daysInMonth = [31, (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0 ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    if (day < 1 || day > daysInMonth[month - 1]) {
      return `Invalid day for month ${month}. Must be between 01-${daysInMonth[month - 1]}.`;
    }

    // Validate year (reasonable range)
    const currentYear = new Date().getFullYear();
    if (year < 1900 || year > currentYear) {
      return `Invalid year. Must be between 1900-${currentYear}.`;
    }

    return '';
  };


// Validation function to check field validity
const validateField = (question, value) => {
  switch (question.field_type) {
    case 'text':
      // Check if this is the KIT Number field
      if (question.question_text === 'KIT Number') {
        return validateKitNumber(value);
      }
      // Check if this is the Phone field
      if (question.question_text === 'Phone') {
        return validatePhoneNumber(value);
      }
      // Check if this is the Whatsapp field
      if (question.question_text === 'Whatsapp') {
        return validatePhoneNumber(value);
      }
      // Check if this is the Birth date field
      if (question.question_text === 'Birth date') {
        return validateBirthDate(value);
      }
      // Validate first name and last name separately
      if (question.id === 'first_name' || question.id === 'last_name') {
        if (value.length > 255) {
          return 'Name must not exceed 255 characters.';
        }
      }
      return validateName(value);
    case 'email':
      return validateEmail(value);
    case 'number':
      return validateNumber(value, question);
    default:
      return ''; // No error for other field types
  }
};

  // Render preview of all responses
  const renderPreview = () => {
    return (
      <div className="preview-content">
        <h3>Review Your Responses</h3>
        <div className="preview-questions">
          {questions.map((question, index) => {
            let answer = responses[question.id] || '';

            // Handle compound name fields
            if (question.field_type === 'compound_name') {
              const firstName = responses[`${question.id}_first`] || '';
              const lastName = responses[`${question.id}_last`] || '';
              answer = `${firstName} ${lastName}`.trim();
            }

            // Handle file responses
            if (question.field_type === 'file' && answer) {
              try {
                const parsedData = JSON.parse(answer);
                if (Array.isArray(parsedData)) {
                  // Multiple files
                  answer = (
                    <div className="multiple-files-preview">
                      <div className="files-count">📎 {parsedData.length} file(s) uploaded</div>
                      {parsedData.map((fileData, fileIndex) => (
                        <div key={fileIndex} className="file-preview">
                          <div className="file-info">
                            <strong>File {fileIndex + 1}:</strong> {fileData.originalName}
                            <br />
                            <small>Size: {(fileData.size / 1024 / 1024).toFixed(2)} MB</small>
                          </div>
                          <div className="file-actions">
                            <button
                              onClick={() => window.open(`${API_BASE.replace('/api', '')}${fileData.url}`, '_blank')}
                              className="file-view-btn"
                            >
                              👁️ View
                            </button>
                            <button
                              onClick={() => {
                                const link = document.createElement('a');
                                link.href = `${API_BASE.replace('/api', '')}/api/files/${fileData.filename}/download`;
                                link.download = fileData.originalName;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                              }}
                              className="file-download-btn"
                            >
                              📥 Download
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                } else {
                  // Single file (backward compatibility)
                  answer = (
                    <div className="file-preview">
                      <div className="file-info">
                        <strong>File:</strong> {parsedData.originalName}
                        <br />
                        <small>Size: {(parsedData.size / 1024 / 1024).toFixed(2)} MB</small>
                      </div>
                      <div className="file-actions">
                        <button
                          onClick={() => window.open(`${API_BASE.replace('/api', '')}${parsedData.url}`, '_blank')}
                          className="file-view-btn"
                        >
                          👁️ View
                        </button>
                        <button
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = `${API_BASE.replace('/api', '')}/api/files/${parsedData.filename}/download`;
                            link.download = parsedData.originalName;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }}
                          className="file-download-btn"
                        >
                          📥 Download
                        </button>
                      </div>
                    </div>
                  );
                }
              } catch (e) {
                // If parsing fails, show as regular text
                answer = answer;
              }
            }

            // Format answer based on field type
            if (question.field_type === 'checkbox' || question.field_type === 'multiselect') {
              if (Array.isArray(answer)) {
                answer = answer.join(', ');
              } else {
                try {
                  const parsed = JSON.parse(answer);
                  if (Array.isArray(parsed)) {
                    answer = parsed.join(', ');
                  }
                } catch (e) {
                  // Keep original if parsing fails
                }
              }
            }

            return (
              <div key={question.id} className="preview-item">
                <div className="preview-question-number">Q{index + 1}</div>
                <div className="preview-question-content">
                  <strong>{question.question_text}</strong>
                  <div className="preview-answer">
                    {answer || <em>No answer provided</em>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Render form field based on type
  const renderFormField = (question) => {
    const value = responses[question.id] || '';
    const isPrefilled = isFieldPrefilled(question.question_number);

    switch (question.field_type) {
      case 'compound_name':
        const firstName = responses[`${question.id}_first`] || '';
        const lastName = responses[`${question.id}_last`] || '';
        return (
          <div className="name-fields">
            <div className="name-field">
              <input
                type="text"
                value={firstName}
                onChange={(e) => {
                  if (isPrefilled) return; // Prevent editing pre-filled fields
                  const value = e.target.value;
                  const newResponses = { ...responses, [`${question.id}_first`]: value };
                  setResponses(newResponses);
                  const error = validateIndividualName(value);
                  setFirstNameError(error);
                  setTimeout(() => saveResponse(`${question.id}_first`, value), 1000);
                }}
                placeholder="First Name"
                className={`form-input ${isPrefilled ? 'prefilled' : ''}`}
                readOnly={isPrefilled}
              />
              <label className="field-label">First Name*</label>
              {firstNameError && <div className="error-alert">{firstNameError}</div>}
            </div>
            <div className="name-field">
              <input
                type="text"
                value={lastName}
                onChange={(e) => {
                  if (isPrefilled) return; // Prevent editing pre-filled fields
                  const value = e.target.value;
                  const newResponses = { ...responses, [`${question.id}_last`]: value };
                  setResponses(newResponses);
                  const error = validateIndividualName(value);
                  setLastNameError(error);
                  setTimeout(() => saveResponse(`${question.id}_last`, value), 1000);
                }}
                placeholder="Last Name"
                className={`form-input ${isPrefilled ? 'prefilled' : ''}`}
                readOnly={isPrefilled}
              />
              <label className="field-label">Last Name*</label>
              {lastNameError && <div className="error-alert">{lastNameError}</div>}
            </div>
          </div>
        );

      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleAnswerChange(e.target.value)}
            placeholder="Enter your response..."
            rows={4}
            className="form-textarea"
          />
        );

      case 'text':
      case 'email':
        // Special handling for birthdate field
        if (question.question_text === 'Birth date') {
          // Parse the date value for the datepicker (convert dd/mm/yyyy to Date object)
          const parseBirthdate = (dateStr) => {
            if (!dateStr) return null;
            const match = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
            if (!match) return null;
            const day = parseInt(match[1]);
            const month = parseInt(match[2]) - 1; // Month is 0-indexed in JS
            const year = parseInt(match[3]);
            return new Date(year, month, day);
          };

          const birthdateValue = parseBirthdate(value);

          return (
            <div className="birthdate-input-wrapper">
              <div className="birthdate-input-container">
                <input
                  type="text"
                  value={value}
                  onChange={(e) => handleAnswerChange(e.target.value)}
                  placeholder="dd/mm/yyyy (e.g., 25/12/1990)"
                  className="birthdate-text-input"
                />
                <button
                  type="button"
                  className="datepicker-icon-btn"
                  onClick={() => setShowBirthdatePicker(!showBirthdatePicker)}
                  title="Open calendar"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                </button>
              </div>
              {showBirthdatePicker && (
                <div className="birthdate-picker-popup">
                  <DatePicker
                    selected={birthdateValue}
                    onChange={(date) => {
                      if (date) {
                        const day = String(date.getDate()).padStart(2, '0');
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const year = date.getFullYear();
                        handleAnswerChange(`${day}/${month}/${year}`);
                      }
                      setShowBirthdatePicker(false);
                    }}
                    dateFormat="dd/MM/yyyy"
                    inline
                    showYearDropdown
                    showMonthDropdown
                    dropdownMode="select"
                    maxDate={new Date()}
                    yearDropdownItemNumber={100}
                    scrollableYearDropdown
                  />
                </div>
              )}
            </div>
          );
        }
        // Special handling for phone fields
        if (question.question_text === 'Phone' || question.question_text === 'Whatsapp') {
          return (
            <div>
              <input
                type="text"
                value={value}
                onChange={(e) => handleAnswerChange(e.target.value)}
                placeholder="Enter phone number with country code"
                className="form-input"
              />
              <div className="field-help-text" style={{
                fontSize: '0.85rem',
                color: '#666',
                marginTop: '8px',
                lineHeight: '1.5'
              }}>
                📱 Include country code with your phone number<br/>
                <strong>Examples:</strong><br/>
                • India: <code>+911234567890</code><br/>
                • USA/Canada: <code>+11234567890</code><br/>
                • UK: <code>+441234567890</code><br/>
                • UAE: <code>+9711234567890</code>
              </div>
            </div>
          );
        }
        return (
          <input
            type={question.field_type}
            value={value}
            onChange={(e) => {
              if (isPrefilled) return; // Prevent editing pre-filled fields
              handleAnswerChange(e.target.value);
            }}
            placeholder={`Enter your ${question.field_type}`}
            className={`form-input ${isPrefilled ? 'prefilled' : ''}`}
            readOnly={isPrefilled}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleAnswerChange(e.target.value)}
            placeholder="Enter a number"
            className="form-input"
            min={question.validation_rules?.min}
            max={question.validation_rules?.max}
            step={question.validation_rules?.step}
          />
        );

      case 'date':
        // Parse the date value if it exists (format: YYYY-MM-DD)
        const dateValue = value ? new Date(value) : null;

        return (
          <DatePicker
            selected={dateValue}
            onChange={(date) => {
              // Format date as YYYY-MM-DD for storage
              const formattedDate = date ? date.toISOString().split('T')[0] : '';
              handleAnswerChange(formattedDate);
            }}
            dateFormat="dd/MM/yyyy"
            placeholderText="Select birth date"
            className="form-input"
            popperPlacement="bottom-start" // ALWAYS open below the input
            popperModifiers={[
              {
                name: 'offset',
                options: {
                  offset: [0, 8], // 8px below the input
                },
              },
              {
                name: 'preventOverflow',
                options: {
                  enabled: false, // Disable auto-flip to prevent opening above
                },
              },
            ]}
            showYearDropdown
            showMonthDropdown
            dropdownMode="select"
            maxDate={new Date()} // Can't select future dates for birth date
            yearDropdownItemNumber={100}
            scrollableYearDropdown
          />
        );

      case 'radio':
        return (
          <div className="radio-group">
            {question.options.map((option, index) => (
              <label key={index} className="radio-label">
                <input
                  type="radio"
                  name={`question-${question.id}`}
                  value={option}
                  checked={value === option}
                  onChange={(e) => handleAnswerChange(e.target.value)}
                />
                <span className="radio-text">{option}</span>
              </label>
            ))}
          </div>
        );

      case 'checkbox':
        const selectedValues = Array.isArray(value) ? value : [];
        const isThreeColumn = question.layout === 'three-column';
        return (
          <div className={`checkbox-group ${isThreeColumn ? 'three-column' : ''}`}>
            {question.options.map((option, index) => (
              <label key={index} className="checkbox-label">
                <input
                  type="checkbox"
                  value={option}
                  checked={selectedValues.includes(option)}
                  onChange={(e) => {
                    const newValues = e.target.checked
                      ? [...selectedValues, option]
                      : selectedValues.filter(v => v !== option);
                    handleAnswerChange(newValues);
                  }}
                />
                <span className="checkbox-text">{option}</span>
              </label>
            ))}
          </div>
        );

      case 'select':
        // Questions that need custom single-select dropdown (to open below, not above)
        const customSingleSelectQuestions = [23, 30, 34, 35, 38, 56, 58, 59];
        const useCustomSingleSelect = customSingleSelectQuestions.includes(question.question_number);

        if (useCustomSingleSelect) {
          // Custom single-select dropdown that always opens below
          return (
            <div className="singleselect-container">
              <div className="singleselect-dropdown">
                <button
                  type="button"
                  className="singleselect-trigger"
                  onClick={() => {
                    setOpenSingleSelectId(openSingleSelectId === question.id ? null : question.id);
                  }}
                >
                  <span>
                    {value || 'Select an option'}
                  </span>
                  <span className="dropdown-arrow">▼</span>
                </button>

                <div
                  id={`singleselect-dropdown-${question.id}`}
                  className="singleselect-options"
                  style={{display: openSingleSelectId === question.id ? 'block' : 'none'}}
                >
                  {question.options.map((option, index) => (
                    <div
                      key={index}
                      className={`singleselect-option ${value === option ? 'selected' : ''}`}
                      onClick={() => {
                        handleAnswerChange(option);
                        setOpenSingleSelectId(null); // Close dropdown after selection
                      }}
                    >
                      <span>{option}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        }

        // Default native select for other questions
        return (
          <select
            value={value}
            onChange={(e) => {
              if (isPrefilled) return; // Prevent editing pre-filled fields
              handleAnswerChange(e.target.value);
            }}
            className={`form-select ${isPrefilled ? 'prefilled' : ''}`}
            disabled={isPrefilled}
          >
            <option value="">Select an option</option>
            {question.options.map((option, index) => (
              <option key={index} value={option}>{option}</option>
            ))}
          </select>
        );

      case 'multiselect':
        const multiSelectedValues = Array.isArray(value) ? value : [];

        return (
          <div className="multiselect-container">
            {multiSelectedValues.length > 0 && (
              <div className="selected-items">
                {multiSelectedValues.map((item, index) => (
                  <div key={index} className="selected-item">
                    <span>{item}</span>
                    <button
                      type="button"
                      onClick={() => {
                        const newValues = multiSelectedValues.filter(v => v !== item);
                        handleAnswerChange(newValues);
                      }}
                      className="remove-item"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="multiselect-dropdown">
              <button
                type="button"
                className="multiselect-trigger"
                onClick={() => {
                  setOpenDropdownId(openDropdownId === question.id ? null : question.id);
                }}
              >
                <span>
                  {multiSelectedValues.length === 0
                    ? 'Select options...'
                    : `${multiSelectedValues.length} selected`
                  }
                </span>
                <span className="dropdown-arrow">▼</span>
              </button>

              <div
                id={`dropdown-${question.id}`}
                className="multiselect-options"
                style={{display: openDropdownId === question.id ? 'block' : 'none'}}
              >
                {question.options.map((option, index) => (
                  <label key={index} className="multiselect-option">
                    <input
                      type="checkbox"
                      checked={multiSelectedValues.includes(option)}
                      onChange={(e) => {
                        const newValues = e.target.checked
                          ? [...multiSelectedValues, option]
                          : multiSelectedValues.filter(v => v !== option);
                        handleAnswerChange(newValues);
                      }}
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        );

      case 'file':
        return (
          <div className="file-upload-container">
            <input
              type="file"
              multiple
              onChange={async (e) => {
                const files = Array.from(e.target.files);
                if (files.length > 0) {
                  try {
                    // Create FormData for multiple file upload
                    const formData = new FormData();
                    files.forEach((file, index) => {
                      formData.append('files', file);
                    });

                    // Upload files to server
                    const uploadResponse = await axios.post(`${API_BASE}/upload`, formData, {
                      headers: {
                        'Content-Type': 'multipart/form-data',
                      },
                    });

                    if (uploadResponse.data.files) {
                      // Store multiple file information as JSON array
                      const filesData = uploadResponse.data.files.map(file => ({
                        filename: file.filename,
                        originalName: file.originalName,
                        url: file.url,
                        size: file.size,
                        type: file.mimetype
                      }));
                      handleAnswerChange(JSON.stringify(filesData));
                    } else if (uploadResponse.data.file) {
                      // Backward compatibility for single file
                      const fileData = {
                        filename: uploadResponse.data.file.filename,
                        originalName: uploadResponse.data.file.originalName,
                        url: uploadResponse.data.file.url,
                        size: uploadResponse.data.file.size,
                        type: uploadResponse.data.file.mimetype
                      };
                      handleAnswerChange(JSON.stringify([fileData]));
                    }
                  } catch (error) {
                    console.error('File upload failed:', error);
                    alert('File upload failed. Please try again.');
                  }
                }
              }}
              className="form-file"
              accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.doc,.docx,.csv,.xlsx,.xls"
            />
            <div className="file-help">
              Supported formats: PDF, Images, Documents (Max 10MB per file, up to 10 files)
            </div>
            {responses[question.id] && (() => {
              try {
                const filesData = JSON.parse(responses[question.id]);
                if (Array.isArray(filesData) && filesData.length > 0) {
                  return (
                    <div className="uploaded-files-list">
                      <h4>Uploaded Files ({filesData.length}):</h4>
                      {filesData.map((file, index) => (
                        <div key={index} className="uploaded-file-item">
                          <span className="file-name">{file.originalName}</span>
                          <span className="file-size">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                        </div>
                      ))}
                    </div>
                  );
                }
              } catch (e) {
                // Ignore parsing errors
              }
              return null;
            })()}
          </div>
        );

      default:
        return <div>Unsupported field type: {question.field_type}</div>;
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  if (questions.length === 0) {
    return (
      <div className="no-questions">
        <h2>No questions available</h2>
        <p>Please run the add-questions script first.</p>
      </div>
    );
  }

  const currentQ = questions[currentQuestion];
  const isLastQuestion = currentQuestion === questions.length - 1;

  return (
    <div className="form-container">
      <div className="form-header">
        <img
          src="/lifecode-logo.png"
          alt="Lifecode"
          className="header-logo"
        />
        <div className="header-content-center">
          <h2 className="header-subtitle">Profile Questionnaire</h2>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${visibleQuestions.length > 0 ? ((visibleQuestions.findIndex(q => q.id === questions[currentQuestion]?.id) + 1) / visibleQuestions.length) * 100 : 0}%` }}
            ></div>
          </div>
          <p className="question-counter">
            Question {visibleQuestions.length > 0 ? visibleQuestions.findIndex(q => q.id === questions[currentQuestion]?.id) + 1 : currentQuestion + 1} of {visibleQuestions.length > 0 ? visibleQuestions.length : questions.length}
          </p>
        </div>
      </div>

      {resumed && (
        <div className="resume-banner">
          👋 Welcome back! We've restored your previous answers. You can review or edit
          any earlier question and continue from where you left off.
        </div>
      )}

      <div className="question-container">
        <h2 className="question-title">
          {currentQuestion + 1}. {currentQ.question_text}
          {currentQ.is_mandatory && <span className="required">*</span>}
        </h2>

        <div className="form-field-container">
          {renderFormField(currentQ)}
        </div>
      </div>

      {/* Error message display - positioned between question container and navigation */}
      {formError && <div className="error-alert-fixed">{formError}</div>}

      <div className="form-navigation">
        <button
          onClick={goToPrev}
          disabled={currentQuestion === 0}
          className="nav-btn prev-btn"
        >
          <ChevronLeft size={20} />
          Prev
        </button>

        <div></div>

        {isLastQuestion ? (
          <button
            onClick={() => {
              if (!isCurrentQuestionAnswered()) {
                setFormError('This field is required to continue.');
                setTimeout(() => setFormError(''), 3000); // Hide error after 3 seconds
                return;
              }
              setShowPreview(true);
            }}
            className="nav-btn submit-btn"
          >
            <Send size={20} />
            Submit
          </button>
        ) : (
          <button
            onClick={handleNextClick}
            className="nav-btn next-btn"
            disabled={isNextDisabled}
          >
            Next
            <ChevronRight size={20} />
          </button>
        )}
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="modal-overlay" onClick={() => setShowPreview(false)}>
          <div className="modal-content preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Form Preview</h2>
              <button
                onClick={() => setShowPreview(false)}
                className="close-btn"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {renderPreview()}
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowPreview(false)}
                className="nav-btn cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowPreview(false);
                  submitForm();
                }}
                className="nav-btn submit-btn"
              >
                <Send size={20} />
                Submit Form
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Form;
